import type { Idioma } from '../../i18n/index.js';

export const AUTORES_EDITORES = {
  es: {
    titulo: 'Para autores y editores',
    entrada: 'Ayudamos a que tu investigación llegue a quienes la necesitan.',
    introduccion:
      'Athena permite a los profesionales de eventos descubrir estudios y encontrar información relevante para su trabajo. Las referencias enlazan a la publicación original, sin coste para sus autores o editores.',
    beneficios: [
      {
        titulo: 'Que te encuentren',
        texto: 'Tu estudio puede aparecer cuando alguien busca un tema que aborda, aunque todavía no conozca su título ni tu organización.',
      },
      {
        titulo: 'Que puedan citarte',
        texto: 'Las referencias con página o ubicación ayudan a utilizar tus hallazgos en propuestas e informes y a consultar su origen.',
      },
      {
        titulo: 'Que lleguen al original',
        texto: 'Enlazamos a tu publicación para que los lectores puedan ampliar la información y conocer el estudio completo. Sin coste para ti.',
      },
    ],
    secciones: [
      {
        titulo: 'Cómo aparece tu investigación',
        texto:
          'Las fichas muestran el título original, la organización editora, el año y el enlace a la publicación cuando están disponibles. Las respuestas incluyen referencias a los estudios utilizados y a la página o ubicación de cada dato.',
      },
      {
        titulo: 'El contexto sigue en el estudio original',
        texto:
          'Las respuestas presentan la información relevante para cada consulta y remiten al estudio original para leer los resultados completos, la metodología y el contexto. Tu investigación también puede consultarse desde Athena y sus conexiones con ChatGPT o Claude, donde los profesionales preparan sus documentos.',
      },
    ],
    contactoTitulo: 'Hablemos de tu publicación',
    contactoTexto:
      'Si eres autor o editor, puedes pedir una corrección de atribución o de enlace, comunicar un error en una respuesta o solicitar que dejemos de utilizar un estudio.',
    contactoDetalle:
      'Indica el título, el enlace original, tu relación con la publicación y el cambio o la consulta que quieres plantear. Revisamos cada petición y podemos pedir más información para entenderla.',
    contactoBoton: 'Escríbenos',
    asunto: 'Consulta sobre una publicación en Athena',
    faqTitulo: 'Preguntas frecuentes',
    preguntas: [
      {
        pregunta: '¿Tiene algún coste aparecer en Athena?',
        respuesta:
          'No cobramos a autores ni editores por aparecer en Athena. Los enlaces a las publicaciones originales son directos, sin etiquetas nofollow ni sponsored.',
      },
      {
        pregunta: '¿Athena sustituye la lectura del estudio?',
        respuesta:
          'No. Una respuesta puede ayudarte a encontrar un dato o conectar información de varios estudios. La publicación original es la referencia para consultar los resultados completos y su contexto.',
      },
      {
        pregunta: '¿Cómo se reconoce la fuente de los datos?',
        respuesta:
          'Cada dato citado remite al estudio y a su página o ubicación. La ficha identifica la organización editora y los detalles disponibles de la publicación. Si falta una atribución o hay un error, puedes escribirnos para que lo revisemos.',
      },
      {
        pregunta: '¿Qué contenido genera la IA?',
        respuesta:
          'Athena genera las respuestas a las consultas a partir de los estudios que encuentra en su catálogo. Las citas literales se presentan aparte, en el idioma del original; los datos de tablas y gráficos se referencian por su página o ubicación. Las respuestas pueden contener errores, por lo que conviene consultar la fuente antes de utilizar un dato importante.',
      },
      {
        pregunta: '¿Dónde puedo leer la publicación completa?',
        respuesta:
          'En el enlace al estudio original que aparece en la ficha y en las referencias de las respuestas. Ese enlace lleva al documento o a la página de publicación; el acceso depende de las condiciones de su editor.',
      },
      {
        pregunta: '¿Puedo solicitar una corrección o una retirada?',
        respuesta:
          'Sí. Puedes escribirnos con los detalles de la publicación y tu petición. Te confirmaremos los cambios que hayamos realizado. No necesitas una cuenta en Athena para contactar con nosotros.',
      },
    ],
    catalogo: 'Explorar los estudios',
    actualizado: 'Última actualización: 13 de octubre de 2026.',
  },
  en: {
    titulo: 'For authors and publishers',
    entrada: 'Helping your research reach the people who need it.',
    introduccion:
      'Athena helps event professionals discover studies and find information relevant to their work. References link to the original publication, at no cost to its authors or publishers.',
    beneficios: [
      {
        titulo: 'Help readers find you',
        texto: 'Your study can appear when someone searches for a topic it covers, even if they do not yet know its title or your organisation.',
      },
      {
        titulo: 'Make your findings easier to cite',
        texto: 'Page or location references help readers use your findings in proposals and reports, and check the original source.',
      },
      {
        titulo: 'Bring readers to the original',
        texto: 'We link to your publication so readers can explore the findings and read the full study. At no cost to you.',
      },
    ],
    secciones: [
      {
        titulo: 'How your research appears',
        texto:
          'Study entries show the original title, publishing organisation, year and publication link where available. Answers include references to the studies used and the page or location of each finding.',
      },
      {
        titulo: 'The full context remains in the original study',
        texto:
          'Answers present information relevant to each question and point to the original study for its full results, methodology and context. Your research can also be consulted through Athena’s connections with ChatGPT or Claude, where professionals prepare their documents.',
      },
    ],
    contactoTitulo: 'Let’s talk about your publication',
    contactoTexto:
      'If you are an author or publisher, you can request an attribution or link correction, report an error in an answer, or ask us to stop using a study.',
    contactoDetalle:
      'Please include the title, original link, your relationship to the publication and the change or concern you would like to raise. We review each request and may ask for further information to understand it.',
    contactoBoton: 'Contact us',
    asunto: 'Enquiry about a publication in Athena',
    faqTitulo: 'Frequently asked questions',
    preguntas: [
      {
        pregunta: 'Is there a cost to appear in Athena?',
        respuesta:
          'We do not charge authors or publishers to appear in Athena. Links to original publications are direct, without nofollow or sponsored tags.',
      },
      {
        pregunta: 'Does Athena replace reading the study?',
        respuesta:
          'No. An answer can help you find a figure or connect findings across studies. The original publication remains the reference for the full results and their context.',
      },
      {
        pregunta: 'How are sources credited?',
        respuesta:
          'Each cited finding refers to the study and its page or location. The study entry identifies the publishing organisation and available publication details. If an attribution is missing or incorrect, you can contact us for a review.',
      },
      {
        pregunta: 'What content does AI generate?',
        respuesta:
          'Athena generates answers to questions using the studies it finds in its catalogue. Direct quotations appear separately, in the original language; findings from tables and charts are referenced by page or location. Answers may contain errors, so please consult the source before using an important finding.',
      },
      {
        pregunta: 'Where can I read the full publication?',
        respuesta:
          'Follow the original study link in the study entry or the answer’s references. It leads to the document or publication page; access depends on the publisher’s conditions.',
      },
      {
        pregunta: 'Can I request a correction or removal?',
        respuesta:
          'Yes. Contact us with the publication details and your request. We will confirm the changes we have made. You do not need an Athena account to contact us.',
      },
    ],
    catalogo: 'Explore the studies',
    actualizado: 'Last updated: 13 October 2026.',
  },
} as const satisfies Record<Idioma, unknown>;
